import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

const SITE_NAME = 'IHGIND';
const SITE_URL = 'https://ihgind.com';
const DEFAULT_TITLE = 'IHGIND | Architectural Metal & Specialist Works';
const DEFAULT_DESCRIPTION = 'Premium architectural metal fabrication in Dubai. Specialists in facades, decorative screens, balustrades, PVD finishes, and bespoke metalwork for architects, designers, and developers across the UAE.';
const DEFAULT_IMAGE = 'https://ihgind.com/og-image.jpg';
const TWITTER_HANDLE = '@ihgind';

/**
 * Comprehensive SEO Meta Component
 * Handles: Title, Description, Canonical URL, Open Graph, Twitter Cards, Structured Data
 */
export const PageMeta = ({ 
  title, 
  description, 
  image,
  type = 'website',
  keywords,
  noindex = false,
  structuredData,
  breadcrumbs
}) => {
  const location = useLocation();
  const canonicalUrl = `${SITE_URL}${location.pathname}`;
  
  useEffect(() => {
    // Title
    const fullTitle = title ? `${title} | ${SITE_NAME}` : DEFAULT_TITLE;
    document.title = fullTitle;
    
    // Get or create helper function
    const setMetaTag = (name, content, isProperty = false) => {
      const attr = isProperty ? 'property' : 'name';
      let meta = document.querySelector(`meta[${attr}="${name}"]`);
      if (!meta) {
        meta = document.createElement('meta');
        meta.setAttribute(attr, name);
        document.head.appendChild(meta);
      }
      meta.setAttribute('content', content);
    };
    
    const setLinkTag = (rel, href) => {
      let link = document.querySelector(`link[rel="${rel}"]`);
      if (!link) {
        link = document.createElement('link');
        link.setAttribute('rel', rel);
        document.head.appendChild(link);
      }
      link.setAttribute('href', href);
    };
    
    // Basic Meta Tags
    const desc = description || DEFAULT_DESCRIPTION;
    setMetaTag('description', desc);
    
    // Keywords (if provided)
    if (keywords) {
      setMetaTag('keywords', keywords);
    }
    
    // Robots
    if (noindex) {
      setMetaTag('robots', 'noindex, nofollow');
    } else {
      setMetaTag('robots', 'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1');
    }
    
    // Canonical URL
    setLinkTag('canonical', canonicalUrl);
    
    // Open Graph Tags
    setMetaTag('og:title', fullTitle, true);
    setMetaTag('og:description', desc, true);
    setMetaTag('og:url', canonicalUrl, true);
    setMetaTag('og:type', type, true);
    setMetaTag('og:site_name', SITE_NAME, true);
    setMetaTag('og:locale', 'en_US', true);
    setMetaTag('og:image', image || DEFAULT_IMAGE, true);
    setMetaTag('og:image:width', '1200', true);
    setMetaTag('og:image:height', '630', true);
    setMetaTag('og:image:alt', fullTitle, true);
    
    // Twitter Card Tags
    setMetaTag('twitter:card', 'summary_large_image');
    setMetaTag('twitter:site', TWITTER_HANDLE);
    setMetaTag('twitter:title', fullTitle);
    setMetaTag('twitter:description', desc);
    setMetaTag('twitter:image', image || DEFAULT_IMAGE);
    
    // Additional SEO Tags
    setMetaTag('author', 'IHGIND');
    setMetaTag('geo.region', 'AE-DU');
    setMetaTag('geo.placename', 'Dubai');
    setMetaTag('geo.position', '25.066667;55.166667');
    setMetaTag('ICBM', '25.066667, 55.166667');
    
    // Structured Data (JSON-LD)
    let scriptTag = document.querySelector('script[data-seo="structured-data"]');
    if (!scriptTag) {
      scriptTag = document.createElement('script');
      scriptTag.setAttribute('type', 'application/ld+json');
      scriptTag.setAttribute('data-seo', 'structured-data');
      document.head.appendChild(scriptTag);
    }
    
    // Build structured data
    const baseStructuredData = {
      "@context": "https://schema.org",
      "@type": "Organization",
      "name": "IHGIND",
      "alternateName": "IHG Industrial",
      "url": SITE_URL,
      "logo": `${SITE_URL}/logo-black.png`,
      "description": DEFAULT_DESCRIPTION,
      "address": {
        "@type": "PostalAddress",
        "addressLocality": "Dubai",
        "addressRegion": "Dubai",
        "addressCountry": "AE"
      },
      "contactPoint": {
        "@type": "ContactPoint",
        "telephone": "+971-4-258-5754",
        "contactType": "sales",
        "email": "sales@ihgind.com",
        "areaServed": ["AE", "SA", "QA", "OM", "BH", "KW"],
        "availableLanguage": ["English", "Arabic"]
      },
      "sameAs": [
        "https://www.linkedin.com/company/ihgind",
        "https://www.instagram.com/ihgind"
      ]
    };
    
    // Add breadcrumb structured data if provided
    let fullStructuredData = structuredData || baseStructuredData;
    
    if (breadcrumbs && breadcrumbs.length > 0) {
      const breadcrumbList = {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        "itemListElement": breadcrumbs.map((crumb, index) => ({
          "@type": "ListItem",
          "position": index + 1,
          "name": crumb.name,
          "item": `${SITE_URL}${crumb.path}`
        }))
      };
      
      // Combine both
      fullStructuredData = [baseStructuredData, breadcrumbList];
    }
    
    scriptTag.textContent = JSON.stringify(fullStructuredData);
    
    // Cleanup function
    return () => {
      // Reset to defaults when component unmounts
      document.title = DEFAULT_TITLE;
    };
  }, [title, description, image, type, keywords, noindex, canonicalUrl, structuredData, breadcrumbs]);

  return null;
};

/**
 * Service/Product Structured Data Helper
 */
export const ServiceStructuredData = ({ name, description, image, provider = 'IHGIND' }) => ({
  "@context": "https://schema.org",
  "@type": "Service",
  "serviceType": name,
  "name": name,
  "description": description,
  "provider": {
    "@type": "Organization",
    "name": provider,
    "url": "https://ihgind.com"
  },
  "areaServed": {
    "@type": "Place",
    "name": "UAE and GCC"
  },
  "image": image
});

/**
 * Article/Blog Post Structured Data Helper
 */
export const ArticleStructuredData = ({ title, description, image, datePublished, author = 'IHGIND Team' }) => ({
  "@context": "https://schema.org",
  "@type": "Article",
  "headline": title,
  "description": description,
  "image": image,
  "datePublished": datePublished,
  "author": {
    "@type": "Person",
    "name": author
  },
  "publisher": {
    "@type": "Organization",
    "name": "IHGIND",
    "logo": {
      "@type": "ImageObject",
      "url": "https://ihgind.com/logo-black.png"
    }
  }
});

/**
 * FAQ Structured Data Helper
 */
export const FAQStructuredData = (faqs) => ({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "mainEntity": faqs.map(faq => ({
    "@type": "Question",
    "name": faq.question,
    "acceptedAnswer": {
      "@type": "Answer",
      "text": faq.answer
    }
  }))
});

/**
 * Local Business Structured Data
 */
export const LocalBusinessStructuredData = () => ({
  "@context": "https://schema.org",
  "@type": "LocalBusiness",
  "name": "IHGIND - Architectural Metal & Specialist Works",
  "image": "https://ihgind.com/og-image.jpg",
  "@id": "https://ihgind.com",
  "url": "https://ihgind.com",
  "telephone": "+971-4-258-5754",
  "priceRange": "$$$$",
  "address": {
    "@type": "PostalAddress",
    "streetAddress": "Jebel Ali Industrial Area 1",
    "addressLocality": "Dubai",
    "addressRegion": "Dubai",
    "postalCode": "000000",
    "addressCountry": "AE"
  },
  "geo": {
    "@type": "GeoCoordinates",
    "latitude": 24.9857,
    "longitude": 55.0272
  },
  "openingHoursSpecification": [
    {
      "@type": "OpeningHoursSpecification",
      "dayOfWeek": ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday"],
      "opens": "08:00",
      "closes": "18:00"
    }
  ],
  "sameAs": [
    "https://www.linkedin.com/company/ihgind",
    "https://www.instagram.com/ihgind"
  ]
});

export default PageMeta;
